import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { toast } from '../common/Toast';
import ReactDOM from 'react-dom';
import './SchnellregistrierungModal.css';
import { passendeDivisionen } from '../../lib/divisionsFilter';
import KategorieAnlegen from '../turnier/KategorienAnlegen';

const SKILL_ORDER = [
  'Beginner', 'Rookie Grappling', 'Amateur',
  'Intermediate', 'Advanced', 'Advanced Grappling',
  'Master Grappling', 'Black Belts', 'Grand Champion',
];

const DIV_TABS = [
  { id: 'alle',      label: 'Alle',              types: null },
  { id: 'formen',    label: 'Formen',             types: ['Formen'] },
  { id: 'selbst',    label: 'Selbstverteidigung', types: ['Selbstverteidigung'] },
  { id: 'kumite',    label: 'Kumite',             types: ['Kumite'] },
  { id: 'kickboxen', label: 'Kickboxen',          types: ['Continous Fighting', 'Pointfighting', 'K1', 'Fullcontact'] },
  { id: 'grappling', label: 'Grappling',          types: ['Grappling'] },
  { id: 'bruchtest', label: 'Bruchtest',          types: ['Bruchtest'] },
  { id: 'rumble',    label: 'Rumble',             types: ['Rumble'] },
];

// Seit 27.09.2026 dieselbe Regel wie Anmeldefenster, QR und Server
// (lib/divisionsFilter.js). Die Abschrift hier ließ im Kampf bis 8 Jahre über
// der Klasse zu. Nachsichtig: was noch nicht eingetippt ist, filtert nicht.
function filterForAthlete(allDiv, profil, stichtag) {
  if (!profil.geschlecht && !profil.geburtsdatum && !profil.gewicht && !profil.skill_level_kata && !profil.skill_level_grappling) return allDiv;
  return passendeDivisionen(allDiv, profil, stichtag, { nachsichtig: true });
}

// Die Startgebühr rechnet der Server (backend/utils/startgebuehr.js). Hier
// stand bis 18.10.2026 eine Abschrift mit erfundenen 25 €/40 €, wenn das
// Turnier keinen Preis hatte, ohne Frühbucher-Zeiträume und mit „Duo“ als
// Einzelstart. Geschickt wird nur noch der Kategorie-Code.

/* ── Hilfsfunktionen ────────────────────────────────────────────────────── */

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function playBeep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.35);
    ctx.close();
  } catch {}
}

function getMyVereinId() {
  const token = localStorage.getItem('token');
  let myVereinId = null;
  try { myVereinId = JSON.parse(atob(token.split('.')[1])).vereins_id; } catch {}
  return myVereinId;
}


function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function SuccessFlash({ name }) {
  return (
    <div className="srm-success-flash">
      <span className="srm-success-check">✓</span>
      <span>{name} registriert!</span>
    </div>
  );
}

/* ── MODUS 1: Express ───────────────────────────────────────────────────── */

const LEER_PROFIL_EXPRESS = {
  vorname: '', nachname: '', geburtsdatum: '', geschlecht: '',
  gewicht: '', skill_level_kata: '', skill_level_grappling: '',
};

function ExpressModus({ turnier, divisionen, stichtag, onRegistered, onAddKategorie }) {
  const [profil, setProfil] = useState({ ...LEER_PROFIL_EXPRESS });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedDivCodes, setSelectedDivCodes] = useState(new Set());
  const [divTab, setDivTab] = useState('alle');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [successName, setSuccessName] = useState(null);
  const [existingId, setExistingId] = useState(null);
  const vorRef = useRef(null);
  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (!debouncedSearch.trim() || debouncedSearch.length < 2) {
      setSearchResults([]);
      return;
    }
    fetch(`/api/wettkaempfer?search=${encodeURIComponent(debouncedSearch)}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setSearchResults(list.slice(0, 8));
      })
      .catch(() => setSearchResults([]));
  }, [debouncedSearch]);

  const fillFromAthlete = (a) => {
    setProfil({
      vorname: a.vorname || '',
      nachname: a.nachname || '',
      geburtsdatum: a.geburtsdatum ? a.geburtsdatum.slice(0, 10) : '',
      geschlecht: a.geschlecht || '',
      gewicht: a.gewicht ? String(a.gewicht) : '',
      skill_level_kata: a.skill_level_kata || '',
      skill_level_grappling: a.skill_level_grappling || '',
    });
    setExistingId(a.wettkaempfer_id || a.id || null);
    setSearchQuery(`${a.vorname} ${a.nachname}`);
    setShowDropdown(false);
    setSelectedDivCodes(new Set());
  };

  const filtered = useMemo(() => filterForAthlete(divisionen, profil, stichtag), [divisionen, profil, stichtag]);
  const tabFiltered = useMemo(() =>
    divTab === 'alle'
      ? filtered
      : filtered.filter(d => {
          const tab = DIV_TABS.find(t => t.id === divTab);
          return tab?.types ? tab.types.includes(d.Division_Type) : true;
        }),
  [filtered, divTab]);

  const toggleDiv = (code) => {
    setSelectedDivCodes(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!profil.vorname.trim() || !profil.nachname.trim()) {
      setFeedback({ type: 'error', msg: 'Vor- und Nachname erforderlich.' });
      return;
    }
    if (selectedDivCodes.size === 0) {
      setFeedback({ type: 'error', msg: 'Bitte mindestens eine Kategorie wählen.' });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      let athleteId = existingId;
      if (!athleteId) {
        const body = {
          vorname: profil.vorname,
          nachname: profil.nachname,
          geburtsdatum: profil.geburtsdatum || null,
          geschlecht: profil.geschlecht || null,
          gewicht: profil.gewicht ? parseFloat(profil.gewicht) : null,
          vereins_id: getMyVereinId(),
          email: null,
          skill_level_kata: profil.skill_level_kata || null,
          skill_level_grappling: profil.skill_level_grappling || null,
        };
        const res = await fetch('/api/wettkaempfer', {
          method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error('Fehler beim Anlegen des Athleten');
        const created = await res.json();
        athleteId = created.wettkaempfer_id || created.id;
      } else {
        const body = {
          vorname: profil.vorname, nachname: profil.nachname,
          geburtsdatum: profil.geburtsdatum || null,
          geschlecht: profil.geschlecht || null,
          gewicht: profil.gewicht ? parseFloat(profil.gewicht) : null,
          skill_level_kata: profil.skill_level_kata || null,
          skill_level_grappling: profil.skill_level_grappling || null,
        };
        await fetch(`/api/wettkaempfer/${athleteId}`, {
          method: 'PUT', headers: getAuthHeaders(), body: JSON.stringify(body),
        });
      }

      const codes = Array.from(selectedDivCodes);
      const divObjects = codes.map(code => ({ division_code: code }));

      const anmRes = await fetch('/api/anmeldungen', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          turnier_id: turnier.turnier_id,
          verein_id: getMyVereinId(),
          wettkampfer_ids: [athleteId],
          wettkampfer_divisionen: [{
            wettkaempfer_id: athleteId,
            divisionen: divObjects,
          }],
        }),
      });
      if (!anmRes.ok) throw new Error('Fehler bei der Anmeldung');

      const name = `${profil.vorname} ${profil.nachname}`;
      setSuccessName(name);
      onRegistered(1);
      setProfil({ ...LEER_PROFIL_EXPRESS });
      setSearchQuery('');
      setSelectedDivCodes(new Set());
      setExistingId(null);
    } catch (e) {
      setFeedback({ type: 'error', msg: e.message || 'Fehler' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="srm-express">
      {successName && (
        <div className="srm-success-bar">
          <SuccessFlash name={successName} />
          <button
            className="srm-btn srm-btn--next"
            onClick={() => {
              setProfil({ ...LEER_PROFIL_EXPRESS });
              setSelectedDivCodes(new Set());
              setExistingId(null);
              setSearchQuery('');
              setSuccessName(null);
              setFeedback(null);
              setTimeout(() => vorRef.current?.focus(), 50);
            }}
          >
            Nächster →
          </button>
        </div>
      )}

      <div className="srm-search-wrap">
        <input
          className="srm-input"
          placeholder="Athleten suchen (bestehend)…"
          value={searchQuery}
          onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true); setExistingId(null); }}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
        />
        {showDropdown && searchResults.length > 0 && (
          <div className="srm-dropdown">
            {searchResults.map(a => (
              <div
                key={a.wettkaempfer_id || a.id}
                className="srm-dropdown-item"
                onMouseDown={() => fillFromAthlete(a)}
              >
                <span className="srm-dropdown-name">{a.vorname} {a.nachname}</span>
                <span className="srm-dropdown-meta">
                  {a.geburtsdatum ? new Date(a.geburtsdatum).getFullYear() : ''}
                  {a.gewicht ? ` · ${a.gewicht} kg` : ''}
                  {a.geschlecht ? ` · ${a.geschlecht}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="srm-form-grid">
        <div className="srm-field">
          <label className="srm-label">Vorname *</label>
          <input ref={vorRef} className="srm-input" value={profil.vorname}
            onChange={e => setProfil(p => ({ ...p, vorname: e.target.value }))} />
        </div>
        <div className="srm-field">
          <label className="srm-label">Nachname *</label>
          <input className="srm-input" value={profil.nachname}
            onChange={e => setProfil(p => ({ ...p, nachname: e.target.value }))} />
        </div>
        <div className="srm-field">
          <label className="srm-label">Geburtsdatum</label>
          <input className="srm-input" type="date" value={profil.geburtsdatum}
            onChange={e => setProfil(p => ({ ...p, geburtsdatum: e.target.value }))} />
        </div>
        <div className="srm-field">
          <label className="srm-label">Gewicht (kg)</label>
          <input className="srm-input" type="number" step="0.1" value={profil.gewicht}
            onChange={e => setProfil(p => ({ ...p, gewicht: e.target.value }))} />
        </div>
        <div className="srm-field srm-field-full">
          <label className="srm-label">Geschlecht</label>
          <div className="srm-radio-row">
            {['männlich', 'weiblich'].map(g => (
              <label key={g} className="srm-radio">
                <input type="radio" name="express-gender" value={g}
                  checked={profil.geschlecht === g}
                  onChange={() => setProfil(p => ({ ...p, geschlecht: g }))} />
                {g.charAt(0).toUpperCase() + g.slice(1)}
              </label>
            ))}
          </div>
        </div>
        <div className="srm-field">
          <label className="srm-label">Skill Kata</label>
          <select className="srm-input" value={profil.skill_level_kata}
            onChange={e => setProfil(p => ({ ...p, skill_level_kata: e.target.value }))}>
            <option value="">— optional —</option>
            {SKILL_ORDER.filter(s => !s.includes('Grappling')).map(s =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="srm-field">
          <label className="srm-label">Skill Grappling</label>
          <select className="srm-input" value={profil.skill_level_grappling}
            onChange={e => setProfil(p => ({ ...p, skill_level_grappling: e.target.value }))}>
            <option value="">— optional —</option>
            {SKILL_ORDER.filter(s => s.includes('Grappling') || s === 'Beginner').map(s =>
              <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="srm-div-section">
        <div className="srm-div-tabs">
          {DIV_TABS.map(t => (
            <button key={t.id} className={`srm-div-tab ${divTab === t.id ? 'active' : ''}`}
              onClick={() => setDivTab(t.id)}>{t.label}</button>
          ))}
          {onAddKategorie && (
            <button className="srm-div-tab srm-div-tab--add" onClick={onAddKategorie} title="Neue Kategorie anlegen">
              + Kategorie
            </button>
          )}
        </div>
        <div className="srm-div-grid">
          {tabFiltered.length === 0 && (
            <p className="srm-empty">Keine passenden Kategorien</p>
          )}
          {tabFiltered.map(d => (
            <label key={d.Division_Code} className={`srm-div-item ${selectedDivCodes.has(d.Division_Code) ? 'selected' : ''}`}>
              <input type="checkbox" checked={selectedDivCodes.has(d.Division_Code)}
                onChange={() => toggleDiv(d.Division_Code)} />
              <span className="srm-div-name">{d.Division_Name}</span>
              <span className="srm-div-type">{d.Division_Type}</span>
            </label>
          ))}
        </div>
        {selectedDivCodes.size > 0 && (
          <div className="srm-selected-count">{selectedDivCodes.size} Kategorie(n) gewählt</div>
        )}
      </div>

      {feedback && (
        <div className={`srm-feedback srm-feedback-${feedback.type}`}>{feedback.msg}</div>
      )}

      <div className="srm-actions">
        <button className="srm-btn srm-btn-primary" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Wird gespeichert…' : '⚡ Speichern & Nächster'}
        </button>
      </div>
    </div>
  );
}

/* ── MODUS 2: Kategorie zuerst ──────────────────────────────────────────── */

function KategorieErstModus({ turnier, divisionen, onRegistered, onAddKategorie }) {
  const [divSearch, setDivSearch] = useState('');
  const [showDivDrop, setShowDivDrop] = useState(false);
  const [selectedDiv, setSelectedDiv] = useState(null);
  const [athleteSearch, setAthleteSearch] = useState('');
  const [athleteResults, setAthleteResults] = useState([]);
  const [showAthleteDrop, setShowAthleteDrop] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [manualProfil, setManualProfil] = useState({ vorname: '', nachname: '', geburtsdatum: '', geschlecht: '', gewicht: '' });
  const [registeredList, setRegisteredList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const debouncedAthleteSearch = useDebounce(athleteSearch, 300);

  const divFiltered = divisionen.filter(d =>
    d.Division_Name.toLowerCase().includes(divSearch.toLowerCase()) ||
    d.Division_Type.toLowerCase().includes(divSearch.toLowerCase())
  ).slice(0, 12);

  useEffect(() => {
    if (!debouncedAthleteSearch.trim() || debouncedAthleteSearch.length < 2) {
      setAthleteResults([]);
      return;
    }
    fetch(`/api/wettkaempfer?search=${encodeURIComponent(debouncedAthleteSearch)}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setAthleteResults(list.slice(0, 8));
      })
      .catch(() => setAthleteResults([]));
  }, [debouncedAthleteSearch]);

  const registerAthlete = async (athleteId, name) => {
    if (!selectedDiv) return;
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await fetch('/api/anmeldungen', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          turnier_id: turnier.turnier_id,
          verein_id: getMyVereinId(),
          wettkampfer_ids: [athleteId],
          wettkampfer_divisionen: [{
            wettkaempfer_id: athleteId,
            divisionen: [{ division_code: selectedDiv.Division_Code }],
          }],
        }),
      });
      if (!res.ok) throw new Error('Anmeldung fehlgeschlagen');
      setRegisteredList(prev => [...prev, { id: athleteId, name }]);
      setAthleteSearch('');
      setAthleteResults([]);
      setShowAthleteDrop(false);
      setManualProfil({ vorname: '', nachname: '', geburtsdatum: '', geschlecht: '', gewicht: '' });
      setManualMode(false);
      onRegistered(1);
    } catch (e) {
      setFeedback({ type: 'error', msg: e.message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleManualSubmit = async () => {
    if (!manualProfil.vorname.trim() || !manualProfil.nachname.trim()) {
      setFeedback({ type: 'error', msg: 'Vor- und Nachname erforderlich.' });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const body = {
        vorname: manualProfil.vorname,
        nachname: manualProfil.nachname,
        geburtsdatum: manualProfil.geburtsdatum || null,
        geschlecht: manualProfil.geschlecht || null,
        gewicht: manualProfil.gewicht ? parseFloat(manualProfil.gewicht) : null,
        vereins_id: getMyVereinId(),
        email: null,
        skill_level_kata: null,
        skill_level_grappling: null,
      };
      const res = await fetch('/api/wettkaempfer', {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Fehler beim Anlegen');
      const created = await res.json();
      const athleteId = created.wettkaempfer_id || created.id;
      await registerAthlete(athleteId, `${manualProfil.vorname} ${manualProfil.nachname}`);
    } catch (e) {
      setFeedback({ type: 'error', msg: e.message });
      setSubmitting(false);
    }
  };

  return (
    <div className="srm-kategorie">
      <div className="srm-field">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
          <label className="srm-label" style={{ margin: 0 }}>Kategorie wählen</label>
          {onAddKategorie && (
            <button type="button" className="srm-div-tab srm-div-tab--add" style={{ fontSize: '0.72rem', padding: '0.2rem 0.55rem' }} onClick={onAddKategorie}>
              + Kategorie
            </button>
          )}
        </div>
        <div className="srm-search-wrap">
          <input
            className="srm-input"
            placeholder="Kategorie suchen…"
            value={selectedDiv ? selectedDiv.Division_Name : divSearch}
            onChange={e => { setDivSearch(e.target.value); setSelectedDiv(null); setShowDivDrop(true); }}
            onFocus={() => setShowDivDrop(true)}
            onBlur={() => setTimeout(() => setShowDivDrop(false), 150)}
          />
          {showDivDrop && divFiltered.length > 0 && !selectedDiv && (
            <div className="srm-dropdown">
              {divFiltered.map(d => (
                <div key={d.Division_Code} className="srm-dropdown-item"
                  onMouseDown={() => { setSelectedDiv(d); setDivSearch(d.Division_Name); setShowDivDrop(false); }}>
                  <span className="srm-dropdown-name">{d.Division_Name}</span>
                  <span className="srm-dropdown-meta">{d.Division_Type} · {d.Gender} · {d.Age_from}–{d.Age_to} J.</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {selectedDiv && (
        <div className="srm-div-info-card">
          <span className="srm-div-info-name">{selectedDiv.Division_Name}</span>
          <div className="srm-div-info-meta">
            <span>{selectedDiv.Division_Type}</span>
            <span>{selectedDiv.Gender}</span>
            <span>{selectedDiv.Age_from}–{selectedDiv.Age_to} Jahre</span>
            {selectedDiv.Minimun_Weight > 0 && <span>{selectedDiv.Minimun_Weight}–{selectedDiv.Maximum_Weight} kg</span>}
            {selectedDiv.Skill_Level && <span>{selectedDiv.Skill_Level}</span>}
          </div>
        </div>
      )}

      {selectedDiv && (
        <>
          <div className="srm-athlete-add">
            <div className="srm-mode-toggle">
              <button className={`srm-toggle-btn ${!manualMode ? 'active' : ''}`}
                onClick={() => setManualMode(false)}>Bestehenden suchen</button>
              <button className={`srm-toggle-btn ${manualMode ? 'active' : ''}`}
                onClick={() => setManualMode(true)}>Neu anlegen</button>
            </div>

            {!manualMode ? (
              <div className="srm-search-wrap" style={{ marginTop: '0.75rem' }}>
                <input
                  className="srm-input"
                  placeholder="Athlet suchen…"
                  value={athleteSearch}
                  onChange={e => { setAthleteSearch(e.target.value); setShowAthleteDrop(true); }}
                  onFocus={() => setShowAthleteDrop(true)}
                  onBlur={() => setTimeout(() => setShowAthleteDrop(false), 150)}
                />
                {showAthleteDrop && athleteResults.length > 0 && (
                  <div className="srm-dropdown">
                    {athleteResults.map(a => (
                      <div key={a.wettkaempfer_id || a.id} className="srm-dropdown-item"
                        onMouseDown={() => registerAthlete(a.wettkaempfer_id || a.id, `${a.vorname} ${a.nachname}`)}>
                        <span className="srm-dropdown-name">{a.vorname} {a.nachname}</span>
                        <span className="srm-dropdown-meta">
                          {a.geburtsdatum ? new Date(a.geburtsdatum).getFullYear() : ''}
                          {a.gewicht ? ` · ${a.gewicht} kg` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="srm-form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="srm-field">
                  <label className="srm-label">Vorname *</label>
                  <input className="srm-input" value={manualProfil.vorname}
                    onChange={e => setManualProfil(p => ({ ...p, vorname: e.target.value }))} />
                </div>
                <div className="srm-field">
                  <label className="srm-label">Nachname *</label>
                  <input className="srm-input" value={manualProfil.nachname}
                    onChange={e => setManualProfil(p => ({ ...p, nachname: e.target.value }))} />
                </div>
                <div className="srm-field">
                  <label className="srm-label">Geburtsdatum</label>
                  <input className="srm-input" type="date" value={manualProfil.geburtsdatum}
                    onChange={e => setManualProfil(p => ({ ...p, geburtsdatum: e.target.value }))} />
                </div>
                <div className="srm-field">
                  <label className="srm-label">Gewicht (kg)</label>
                  <input className="srm-input" type="number" step="0.1" value={manualProfil.gewicht}
                    onChange={e => setManualProfil(p => ({ ...p, gewicht: e.target.value }))} />
                </div>
                <div className="srm-field srm-field-full">
                  <label className="srm-label">Geschlecht</label>
                  <div className="srm-radio-row">
                    {['männlich', 'weiblich'].map(g => (
                      <label key={g} className="srm-radio">
                        <input type="radio" name="kat-gender" value={g}
                          checked={manualProfil.geschlecht === g}
                          onChange={() => setManualProfil(p => ({ ...p, geschlecht: g }))} />
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="srm-field srm-field-full">
                  <button className="srm-btn srm-btn-primary" onClick={handleManualSubmit} disabled={submitting}>
                    {submitting ? 'Wird gespeichert…' : '+ Anlegen & Anmelden'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {feedback && (
            <div className={`srm-feedback srm-feedback-${feedback.type}`}>{feedback.msg}</div>
          )}

          {registeredList.length > 0 && (
            <div className="srm-registered-list">
              <div className="srm-registered-header">In dieser Session angemeldet ({registeredList.length}):</div>
              {registeredList.map((item, i) => (
                <div key={i} className="srm-registered-item">
                  <span className="srm-check-icon">✓</span> {item.name}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── MODUS 3: Vereinsliste ──────────────────────────────────────────────── */

function VereinslisteModus({ turnier, divisionen, stichtag, onRegistered }) {
  const [vereine, setVereine] = useState([]);
  const [selectedVereinId, setSelectedVereinId] = useState('');
  const [athletes, setAthletes] = useState([]);
  const [rows, setRows] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(null);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    fetch('/api/vereine', { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setVereine(Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedVereinId) { setAthletes([]); setRows({}); return; }
    fetch(`/api/vereine/${selectedVereinId}/wettkaempfer`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
        setAthletes(list);
        const init = {};
        list.forEach(a => {
          const id = a.wettkaempfer_id || a.id;
          init[id] = { selected: false, divCode: '', divCode2: '' };
        });
        setRows(init);
      })
      .catch(() => {});
  }, [selectedVereinId]);

  const selectAll = () => setRows(prev => {
    const next = { ...prev };
    Object.keys(next).forEach(id => { next[id] = { ...next[id], selected: true }; });
    return next;
  });

  const selectNone = () => setRows(prev => {
    const next = { ...prev };
    Object.keys(next).forEach(id => { next[id] = { ...next[id], selected: false }; });
    return next;
  });

  const getDivOptionsForAthlete = useCallback((athlete) => {
    const profil = {
      geburtsdatum: athlete.geburtsdatum ? athlete.geburtsdatum.slice(0, 10) : '',
      geschlecht: athlete.geschlecht || '',
      gewicht: athlete.gewicht ? String(athlete.gewicht) : '',
      skill_level_kata: athlete.skill_level_kata || '',
      skill_level_grappling: athlete.skill_level_grappling || '',
    };
    return filterForAthlete(divisionen, profil, stichtag);
  }, [divisionen, stichtag]);

  const handleSubmit = async () => {
    const selectedAthletes = athletes.filter(a => rows[a.wettkaempfer_id || a.id]?.selected);
    if (selectedAthletes.length === 0) {
      setFeedback({ type: 'error', msg: 'Keine Athleten ausgewählt.' });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    let count = 0;
    const fehler = [];
    for (let i = 0; i < selectedAthletes.length; i++) {
      const a = selectedAthletes[i];
      const id = a.wettkaempfer_id || a.id;
      const row = rows[id];
      setProgress(`${i + 1}/${selectedAthletes.length}: ${a.vorname} ${a.nachname}`);
      const divs = [row.divCode, row.divCode2].filter(Boolean);
      if (divs.length === 0) continue;
      const divObjects = divs.map(code => ({ division_code: code }));
      try {
        const res = await fetch('/api/anmeldungen', {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            turnier_id: turnier.turnier_id,
            verein_id: selectedVereinId,
            wettkampfer_ids: [id],
            wettkampfer_divisionen: [{ wettkaempfer_id: id, divisionen: divObjects }],
          }),
        });
        if (res.ok) count++;
        else {
          const d = await res.json().catch(() => ({}));
          fehler.push(`${a.vorname} ${a.nachname}: ${d.error || `Fehler ${res.status}`}`);
        }
      } catch (e) { fehler.push(`${a.vorname} ${a.nachname}: ${e.message}`); }
    }
    setProgress(null);
    setSubmitting(false);
    setFeedback(fehler.length
      ? { type: 'error', msg: `${count} angemeldet, ${fehler.length} nicht: ${fehler.join(' · ')}` }
      : { type: 'ok', msg: `${count} Athlet(en) erfolgreich angemeldet.` });
    onRegistered(count);
  };

  return (
    <div className="srm-verein">
      <div className="srm-field">
        <label className="srm-label">Verein</label>
        <select className="srm-input" value={selectedVereinId}
          onChange={e => setSelectedVereinId(e.target.value)}>
          <option value="">— Verein wählen —</option>
          {vereine.map(v => (
            <option key={v.vereins_id} value={v.vereins_id}>{v.name}</option>
          ))}
        </select>
      </div>

      {athletes.length > 0 && (
        <>
          <div className="srm-bulk-actions">
            <button className="srm-btn srm-btn-sm" onClick={selectAll}>Alle auswählen</button>
            <button className="srm-btn srm-btn-sm" onClick={selectNone}>Keine</button>
            <span className="srm-bulk-count">
              {Object.values(rows).filter(r => r.selected).length} / {athletes.length} gewählt
            </span>
          </div>

          <div className="srm-table-wrap">
            <table className="srm-table">
              <thead>
                <tr>
                  <th>✓</th>
                  <th>Name</th>
                  <th>Geb.</th>
                  <th>Gew.</th>
                  <th>Kategorie 1</th>
                  <th>Kategorie 2</th>
                </tr>
              </thead>
              <tbody>
                {athletes.map(a => {
                  const id = a.wettkaempfer_id || a.id;
                  const row = rows[id] || {};
                  const divOptions = getDivOptionsForAthlete(a);
                  return (
                    <tr key={id} className={row.selected ? 'selected' : ''}>
                      <td>
                        <input type="checkbox" checked={!!row.selected}
                          onChange={e => setRows(prev => ({
                            ...prev, [id]: { ...prev[id], selected: e.target.checked }
                          }))} />
                      </td>
                      <td>{a.vorname} {a.nachname}</td>
                      <td>{a.geburtsdatum ? new Date(a.geburtsdatum).getFullYear() : '—'}</td>
                      <td>{a.gewicht ? `${a.gewicht} kg` : '—'}</td>
                      <td>
                        <select className="srm-table-select" value={row.divCode || ''}
                          onChange={e => setRows(prev => ({
                            ...prev, [id]: { ...prev[id], divCode: e.target.value }
                          }))}>
                          <option value="">—</option>
                          {divOptions.map(d => (
                            <option key={d.Division_Code} value={d.Division_Code}>{d.Division_Name}</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select className="srm-table-select" value={row.divCode2 || ''}
                          onChange={e => setRows(prev => ({
                            ...prev, [id]: { ...prev[id], divCode2: e.target.value }
                          }))}>
                          <option value="">—</option>
                          {divOptions.map(d => (
                            <option key={d.Division_Code} value={d.Division_Code}>{d.Division_Name}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {progress && (
            <div className="srm-progress">Registriere: {progress}</div>
          )}
          {feedback && (
            <div className={`srm-feedback srm-feedback-${feedback.type}`}>{feedback.msg}</div>
          )}

          <div className="srm-actions">
            <button className="srm-btn srm-btn-primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? `Registriere…` : '🏫 Verein registrieren'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/* ── MODUS 4: QR Self-Service ───────────────────────────────────────────── */

function QRSelfServiceModus({ turnier, onRegistered }) {
  const [token, setToken] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [pending, setPending] = useState([]);
  const [warnings, setWarnings] = useState({});
  const [vereinName, setVereinName] = useState('');
  const [vereine, setVereine] = useState([]);
  const [pendingVereins, setPendingVereins] = useState({});
  // Aktuelle Vereinsliste für das Polling (sonst sähe die Schleife nur die leere Startliste)
  const vereineRef = useRef([]);
  const [voranmeldungToken, setVoranmeldungToken] = useState(null);
  const [voranmeldungLoading, setVoranmeldungLoading] = useState(false);
  const prevPendingLength = useRef(0);

  useEffect(() => {
    fetch('/api/vereine', { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        // Platzhalter „Ohne Verein“ steht als eigene Wahl unten, nicht doppelt
        const liste = (Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []))
          .filter(v => v.rolle !== 'ohne_verein');
        vereineRef.current = liste;
        setVereine(liste);
      })
      .catch(() => {});
  }, []);

  // Fetch session token from backend on mount
  useEffect(() => {
    fetch('/api/quickreg/session', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ turnier_id: turnier.turnier_id }),
    })
      .then(r => r.json())
      .then(data => {
        setToken(data.token);
        setTokenLoading(false);
      })
      .catch(() => setTokenLoading(false));
  }, [turnier.turnier_id]);

  const url = token
    ? `${window.location.origin}/quick-reg/${token}${vereinName.trim() ? `?verein=${encodeURIComponent(vereinName.trim())}` : ''}`
    : '';

  // Abfrage je TURNIER, nicht je Code (18.10.2026): jedes Öffnen erzeugt
  // einen neuen Code, Anmeldungen über ältere oder ausgedruckte Codes und den
  // Voranmeldungs-Link sollen trotzdem hier stehen.
  const [pollFehler, setPollFehler] = useState('');
  useEffect(() => {
    if (!turnier?.turnier_id) return;
    const poll = () => {
      fetch(`/api/quickreg/pending?turnier_id=${encodeURIComponent(turnier.turnier_id)}`, { headers: getAuthHeaders() })
        .then(async r => {
          const data = await r.json().catch(() => ({}));
          if (!r.ok || !data.success) throw new Error(data.error || `Fehler ${r.status}`);
          setPollFehler('');
          return data;
        })
        .then(data => {
          const newData = Array.isArray(data?.data) ? data.data : [];
          setPending(newData);
          if (newData.length > prevPendingLength.current) {
            playBeep();
            // initialize verein selection for newly arrived items
            setPendingVereins(prev => {
              const next = { ...prev };
              newData.forEach(p => {
                // Aufgabe 127: Hat der Athlet einen gespeicherten Verein gewählt
                // (gleicher Name), diesen vorwählen. Sonst „Ohne Verein“
                // (Entscheidung 18.10.2026), NICHT der eigene Verein.
                const name = (p.verein_name || '').trim().toLowerCase();
                const treffer = name && vereineRef.current.find(v => (v.name || '').trim().toLowerCase() === name);
                if (!(p.id in next)) next[p.id] = (treffer && String(treffer.vereins_id)) || 'ohne';
              });
              return next;
            });
          }
          prevPendingLength.current = newData.length;
        })
        .catch((e) => setPollFehler(e.message === 'Failed to fetch' ? 'Server nicht erreichbar' : e.message));
    };
    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [turnier?.turnier_id]);

  const confirm = async (id) => {
    try {
      const res = await fetch(`/api/quickreg/confirm/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ verein_id: pendingVereins[id] || 'ohne' }),
      });
      const data = await res.json().catch(() => ({}));
      // Fehler sichtbar machen (vorher still verschluckt, 18.10.2026)
      if (!res.ok || !data.success) throw new Error(data.error || `Fehler ${res.status}`);
      // Am Turniertag: gleich in Pool/Startliste einsortiert? (quickreg einsortiert)
      if (data.einsortiert) {
        const rein = data.einsortiert.eingetragen || [];
        const offen = data.einsortiert.offen || [];
        if (rein.length) toast.ok(`Eingeteilt: ${rein.map((e) => `${e.division_code} (${e.wo})`).join(', ')}`, 7000);
        if (offen.length) toast.fehler(`Nicht automatisch eingeteilt: ${offen.map((o) => `${o.division_code || ''} ${o.grund}`.trim()).join(', ')}. Steht in der Leitstelle.`, 9000);
      }
      if (data.duplicates?.length > 0) {
        setWarnings(prev => ({ ...prev, [id]: data.duplicates }));
        setTimeout(() => {
          setWarnings(prev => { const n = { ...prev }; delete n[id]; return n; });
          setPending(prev => prev.filter(p => p.id !== id));
          onRegistered(1);
        }, 3000);
      } else {
        setPending(prev => prev.filter(p => p.id !== id));
        onRegistered(1);
      }
    } catch (e) {
      toast.fehler(`Bestätigen fehlgeschlagen: ${e.message === 'Failed to fetch' ? 'Server nicht erreichbar' : e.message}`);
    }
  };

  const reject = async (id) => {
    try {
      const res = await fetch(`/api/quickreg/reject/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) throw new Error(data.error || `Fehler ${res.status}`);
      setPending(prev => prev.filter(p => p.id !== id));
    } catch (e) {
      toast.fehler(`Ablehnen fehlgeschlagen: ${e.message === 'Failed to fetch' ? 'Server nicht erreichbar' : e.message}`);
    }
  };

  return (
    <div className="srm-qr">
      <div className="srm-qr-verein-row">
        <label className="srm-label">Verein vorausfüllen (optional)</label>
        <input
          className="srm-input srm-qr-verein-input"
          placeholder="z.B. TDA München – wird im Formular vorausgefüllt"
          value={vereinName}
          onChange={e => setVereinName(e.target.value)}
        />
      </div>

      <div className="srm-qr-top">
        <div className="srm-qr-code-wrap">
          {tokenLoading ? (
            <div className="srm-qr-loading">QR-Code wird generiert…</div>
          ) : (
            <>
              <img
                className="srm-qr-img"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(url)}`}
                alt="QR-Code"
              />
              <div className="srm-qr-label">Self-Service Link</div>
              <div className="srm-qr-url">{url}</div>
            </>
          )}
        </div>
        <div className="srm-qr-info">
          <p>Athleten können sich über den QR-Code selbst für dieses Turnier anmelden.</p>
          <p>Eingehende Anmeldungen erscheinen unten und müssen von dir bestätigt werden.</p>
          <div className="srm-qr-pulse">
            <span className="srm-qr-dot"></span> Warte auf Anmeldungen…
          </div>
        </div>
      </div>

      {/* Voranmeldungs-Link für Vereine */}
      <div className="srm-voranmeldung-section">
        <div className="srm-label">Voranmeldungs-Link für Vereine (7 Tage gültig)</div>
        {voranmeldungToken ? (
          <div className="srm-voranmeldung-url-wrap">
            <input
              className="srm-input srm-voranmeldung-url"
              readOnly
              value={`${window.location.origin}/voranmeldung/${voranmeldungToken}${vereinName.trim() ? `?verein=${encodeURIComponent(vereinName.trim())}` : ''}`}
              onFocus={e => e.target.select()}
            />
            <button
              className="srm-btn srm-btn-sm"
              onClick={() => {
                const url = `${window.location.origin}/voranmeldung/${voranmeldungToken}${vereinName.trim() ? `?verein=${encodeURIComponent(vereinName.trim())}` : ''}`;
                navigator.clipboard?.writeText(url);
              }}
            >📋</button>
          </div>
        ) : (
          <button
            className="srm-btn srm-btn-sm"
            disabled={voranmeldungLoading}
            onClick={async () => {
              setVoranmeldungLoading(true);
              try {
                const res = await fetch('/api/quickreg/voranmeldung-session', {
                  method: 'POST',
                  headers: getAuthHeaders(),
                  body: JSON.stringify({ turnier_id: turnier.turnier_id }),
                });
                const data = await res.json().catch(() => ({}));
                // Fehler sichtbar machen (vorher still verschluckt, 19.10.2026)
                if (!res.ok || !data.success || !data.token) throw new Error(data.error || `Fehler ${res.status}`);
                setVoranmeldungToken(data.token);
              } catch (e) {
                toast.fehler(`Voranmeldungs-Link nicht erzeugt: ${e.message === 'Failed to fetch' ? 'Server nicht erreichbar' : e.message}`);
              }
              setVoranmeldungLoading(false);
            }}
          >
            {voranmeldungLoading ? 'Wird generiert…' : '🔗 Link generieren'}
          </button>
        )}
      </div>

      {pollFehler && (
        <div className="srm-dup-warning">Ausstehende Anmeldungen konnten nicht geladen werden: {pollFehler}</div>
      )}

      {pending.length > 0 && (
        <div className="srm-pending-list">
          <div className="srm-pending-header">Ausstehende Bestätigungen ({pending.length})</div>
          {pending.map(p => (
            <div key={p.id} className="srm-pending-item">
              <div className="srm-pending-info">
                <span className="srm-pending-name">{p.vorname} {p.nachname}</span>
                {p.geburtsdatum && (
                  <span className="srm-pending-meta">
                    {new Date(p.geburtsdatum).getFullYear()} · {p.geschlecht}
                  </span>
                )}
                {p.divisionen && p.divisionen.length > 0 && (
                  <div className="srm-pending-divs">
                    {p.divisionen.map((d, i) => (
                      <span key={i} className="srm-pending-div-tag">{d.division_name || d.division_code}</span>
                    ))}
                  </div>
                )}
                {p.verein_name && pendingVereins[p.id] === 'ohne' && (
                  <span className="srm-pending-meta">Angegeben: „{p.verein_name}“ (nicht gefunden)</span>
                )}
                {(p.link_art === 'voranmeldung' || p.link_abgelaufen) && (
                  <span className="srm-pending-meta">
                    {p.link_art === 'voranmeldung' ? 'über Voranmeldungs-Link' : 'über einen älteren QR-Code'}
                  </span>
                )}
                {warnings[p.id] && (
                  <div className="srm-dup-warning">⚠️ {warnings[p.id].join(', ')} bereits angemeldet</div>
                )}
              </div>
              <div className="srm-pending-actions">
                <select
                  className="srm-input srm-pending-verein-select"
                  aria-label="Verein"
                  value={pendingVereins[p.id] || 'ohne'}
                  onChange={e => setPendingVereins(prev => ({ ...prev, [p.id]: e.target.value }))}
                >
                  <option value="ohne">Ohne Verein</option>
                  {vereine.map(v => (
                    <option key={v.vereins_id} value={v.vereins_id}>{v.name}</option>
                  ))}
                </select>
                <button className="srm-btn srm-btn-confirm" onClick={() => confirm(p.id)}>✓ Bestätigen</button>
                <button className="srm-btn srm-btn-reject" onClick={() => reject(p.id)}>✗</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pending.length === 0 && (
        <div className="srm-pending-empty">Noch keine ausstehenden Anmeldungen.</div>
      )}
    </div>
  );
}

/* ── Haupt-Modal ────────────────────────────────────────────────────────── */

const MODES = [
  { id: 'express',  icon: '⚡', label: 'Express' },
  { id: 'kategorie',icon: '🏷', label: 'Kategorie' },
  { id: 'verein',   icon: '🏫', label: 'Verein' },
  { id: 'qr',       icon: '📱', label: 'Self-Service' },
];

export default function SchnellregistrierungModal({ turnier, onClose }) {
  const [mode, setMode] = useState('express');
  const [divisionen, setDivisionen] = useState([]);
  const [stichtag, setStichtag] = useState(null);   // Alter wie im Anmeldefenster: am Stichtag des Turniers
  const [sessionCount, setSessionCount] = useState(0);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [showAddKategorie, setShowAddKategorie] = useState(false);

  useEffect(() => {
    const onOffline = () => setIsOffline(true);
    const onOnline  = () => setIsOffline(false);
    window.addEventListener('offline', onOffline);
    window.addEventListener('online',  onOnline);
    return () => { window.removeEventListener('offline', onOffline); window.removeEventListener('online', onOnline); };
  }, []);

  const loadDivisionen = useCallback(() => {
    if (!turnier?.turnier_id) return;
    const cacheKey = `srm-div-cache-${turnier.turnier_id}`;
    fetch(`/api/divisionen?turnierId=${turnier.turnier_id}&limit=2000`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        const rows = Array.isArray(data?.data) ? data.data : [];
        setDivisionen(rows);
        setStichtag(data?.altersStichtag || null);
        try { localStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: rows })); } catch {}
      })
      .catch(() => {
        try {
          const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
          if (cached && Date.now() - cached.ts < 86400000) setDivisionen(cached.data);
        } catch {}
      });
  }, [turnier?.turnier_id]);

  useEffect(() => { loadDivisionen(); }, [loadDivisionen]);

  const handleRegistered = useCallback((count = 1) => {
    setSessionCount(prev => prev + count);
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return ReactDOM.createPortal(
    <>
    <div className="srm-overlay" onClick={handleOverlayClick}>
      <div className="srm-modal" role="dialog" aria-modal="true">
        <div className="srm-modal-header">
          <div className="srm-header-left">
            <h2 className="srm-title">⚡ Schnellregistrierung</h2>
            <span className="srm-turnier-name">{turnier.name}</span>
          </div>
          <div className="srm-header-right">
            {sessionCount > 0 && (
              <span className="srm-session-badge">{sessionCount} registriert ✓</span>
            )}
            <button className="srm-close-btn" onClick={onClose} aria-label="Schließen">✕</button>
          </div>
        </div>

        <div className="srm-mode-tabs">
          {MODES.map(m => (
            <button
              key={m.id}
              className={`srm-mode-tab ${mode === m.id ? 'active' : ''}`}
              onClick={() => setMode(m.id)}
            >
              <span className="srm-mode-icon">{m.icon}</span>
              <span className="srm-mode-label">{m.label}</span>
            </button>
          ))}
        </div>

        {isOffline && (
          <div className="srm-offline-banner">
            ⚠️ Offline – Kategorien aus Cache geladen
          </div>
        )}

        <div className="srm-modal-body">
          {mode === 'express'   && <ExpressModus    turnier={turnier} divisionen={divisionen} stichtag={stichtag} onRegistered={handleRegistered} onAddKategorie={() => setShowAddKategorie(true)} />}
          {mode === 'kategorie' && <KategorieErstModus turnier={turnier} divisionen={divisionen} onRegistered={handleRegistered} onAddKategorie={() => setShowAddKategorie(true)} />}
          {mode === 'verein'    && <VereinslisteModus  turnier={turnier} divisionen={divisionen} stichtag={stichtag} onRegistered={handleRegistered} />}
          {mode === 'qr'        && <QRSelfServiceModus turnier={turnier} onRegistered={handleRegistered} />}
        </div>
      </div>
    </div>

    <KategorieAnlegen
      isOpen={showAddKategorie}
      onClose={() => setShowAddKategorie(false)}
      refreshKategorien={() => { loadDivisionen(); setShowAddKategorie(false); }}
      turnier={turnier}
    />
    </>,
    document.body
  );
}
